import React, { useEffect, useRef, useState } from "react";
import AppBreadcrumb from "../../components/CustomeBreadcrumb/AppBreadcrumb";
import { getSpecificItemDetails } from "../../service/ProductService";
import { GET_DASHBOARD_DATA } from "../../config/ApiConstant";
import { Skeleton } from "primereact/skeleton";
import { Link } from "react-router-dom";
import { Chart } from "primereact/chart";
import { Calendar } from "primereact/calendar";

const EmployeeDashboard = () => {
  document.title = "Employee Dashboard | Nexawage Payroll";

  const toast = useRef<any>(null);

  // dashboard data obj
  const [dashboardData, setDashboardData] = useState<any>();

  // page load obj
  const [pageLoad, setPageLoad] = useState<boolean>(false);

  // year obj
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<Date | null>(new Date());

  // graph obj
  const [barOptions, setBarOptions] = useState<any>(null);
  const [barChartData, setBarChartData] = useState<any>(null);

  // get userData from local storage
  const rawUserData = localStorage.getItem("user_data");
  let userData: any = null;
  userData = rawUserData ? JSON.parse(rawUserData) : null;
  const userRole = userData?.roles?.role_name || "";

  //   useEffect to get dashboard data
  useEffect(() => {
    getDashboard();
  }, []);

  // bar chart
  useEffect(() => {
    prepareGrossPaymentChart(dashboardData, setBarChartData, setBarOptions);
  }, [dashboardData]);

  // display bar chart values
  const prepareGrossPaymentChart = (
    dashboardData: any,
    setBarChartData: (data: any) => void,
    setBarOptions: (options: any) => void
  ) => {
    const payments = dashboardData?.gross_payment || [];
    if (payments.length > 0) {
      const values = payments.map((item: any) =>
        parseFloat(item.gross_payment)
      );

      // label format
      const labels = payments.map((item: any) => item.month);

      // bar chart data
      const dynamicBarData = {
        labels,
        datasets: [
          {
            label: "Gross Payment",
            backgroundColor: "rgb(54, 162, 235)",
            borderColor: "rgb(54, 162, 235)",
            data: values,
          },
        ],
      };

      // bar chart option
      const dynamicBarOptions = {
        maintainAspectRatio: false,
        aspectRatio: 0.6,
        responsive: true,
        plugins: {
          legend: {
            position: "top",
          },
          title: {
            display: true,
            text: "Gross Payments Over Time",
          },
          tooltip: {
            callbacks: {
              label: (ctx: any) => `$${ctx.raw?.toFixed(2) || 0}`,
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: (value: number) => `$${value}`,
            },
          },
          x: {
            ticks: {
              autoSkip: true,
              maxRotation: 45,
              minRotation: 0,
            },
          },
        },
      };

      setBarChartData(dynamicBarData);
      setBarOptions(dynamicBarOptions);
    } else {
      setBarChartData(null);
      setBarOptions(null);
    }
  };

  // get dashboard data
  const getDashboard = async (year = currentYear) => {
    try {
      const response = await getSpecificItemDetails(
        GET_DASHBOARD_DATA,
        "year",
        year
      );
      const data = response.data;

      if (data) {
        setDashboardData(data);
        setPageLoad(true);
      } else {
        setDashboardData({});
        setPageLoad(false);
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message,
      });
      setPageLoad(false);
    }
  };
  return (
    <>
      <AppBreadcrumb
        pageTitle="Dashboard"
        pageName="Dashboard"
        currentMenu="Employee"
      />
      <div className="layout-dashboard">
        <div className={`flex align-items-center justify-content-end`}>
          <Calendar
            id="year"
            value={selectedYear}
            onChange={(e) => {
              if (e.value instanceof Date) {
                setSelectedYear(e.value);
              } else {
                setSelectedYear(null);
              }
            }}
            view="year"
            dateFormat="yy"
            showIcon
            className="w-auto"
            placeholder="Select Year"
          />
        </div>
        {/* total paystub */}
        <div className="grid mt-2">
          <div className="col-12 md:col-6 lg:col-4">
            <Link to="/paystub">
              <div className="card dashbaord-main-box">
                <div className="small-box bg-info">
                  <div
                    className="inner"
                    style={{ borderLeft: "3px solid #4650dd" }}
                  >
                    <div className="flex flex-column justify-content-between">
                      <h2 className="mb-1 title-text">Total PayStub</h2>
                      <div className="flex align-items-center justify-content-between">
                        <h3
                          className="mt-0 mb-0 num-text"
                          style={{ color: "#4650dd" }}
                        >
                          {pageLoad ? (
                            !window.cn(dashboardData) &&
                            !window.cn(
                              dashboardData.employee_pay_stub_count
                            ) ? (
                              dashboardData.employee_pay_stub_count
                            ) : (
                              ""
                            )
                          ) : (
                            <Skeleton
                              width="5rem"
                              height="2.5rem"
                              className="rounded"
                            />
                          )}
                        </h3>
                        <div
                          className="dashboard-icon"
                          style={{
                            padding: "0.5rem",
                            backgroundColor: "#dadcf8",
                            borderRadius: "9999px",
                            color: "#4650dd",
                            width: "40px",
                            height: "40px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            className="lucide lucide-file-text"
                          >
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                            <line x1="16" y1="13" x2="8" y2="13" />
                            <line x1="16" y1="17" x2="8" y2="17" />
                            <line x1="10" y1="9" x2="8" y2="9" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </div>

        {/* if userrole is employee then bar graph will visible */}
        <div className="grid">
          <div className="col-12">
            <div className="card bar-chart-wrapper">
              <div className="flex justify-content-between align-items-center">
                <h5 className="m-0">Gross Payment</h5>
              </div>

              {pageLoad ? (
                barChartData ? (
                  <Chart type="line" data={barChartData} options={barOptions} />
                ) : (
                  <div className="flex gap-1 p-4 align-items-center flex-column justify-content-center ">
                    <img
                      src="https://res.cloudinary.com/dpc2muwar/image/upload/v1753697918/no-datas_voktda.png"
                      alt=""
                      width="120"
                      height="120"
                      style={{ marginBottom: "-8px" }}
                    />
                    <p className="text-gray-500">
                      No Gross Payment data available for the selected year.
                    </p>
                  </div>
                )
              ) : (
                <Skeleton height="300px" className="mt-3 mb-3 border-round" />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EmployeeDashboard;
